/**
 * Context pour la gestion du profil utilisateur
 * @version 2.0.0 - Firebase Integration
 * @date 2025-11-06
 */

import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { UserProfile, UserResponse, ProgressStats } from "../types";
import { useFirebaseAuth } from "../hooks/useFirebaseAuth";
import { useProgress, useTestResults } from "../hooks/useFirestore";
import { createOrUpdateUserProfile } from "../firebase/firestoreService";

interface UserContextType {
  user: UserProfile | null;
  token: string | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
  // Legacy login for backward compatibility
  login: (token: string, userData: any) => void;
  // Firebase authentication methods
  firebaseLogin: (email: string, password: string) => Promise<any>;
  firebaseRegister: (email: string, password: string, displayName: string) => Promise<any>;
  firebaseLogout: () => Promise<any>;
  googleSignIn: () => Promise<any>;
  logout: () => void;
  setUser: (user: UserProfile) => void;
  responses: UserResponse[];
  addResponse: (response: UserResponse) => void;
  stats: ProgressStats;
  updateStats: () => void;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Firebase authentication hook
  const firebaseAuth = useFirebaseAuth();

  const [user, setUser] = useState<UserProfile | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [responses, setResponses] = useState<UserResponse[]>([]);
  const [stats, setStats] = useState<ProgressStats>({
    totalExercises: 0,
    completedExercises: 0,
    averageScore: 0,
    timeSpent: 0,
    streakDays: 0,
    levelProgress: {},
    domainProgress: {},
  });

  // Firebase data hooks (only active when user is authenticated)
  const { progress, updateProgress } = useProgress(
    firebaseAuth.user?.uid || null,
    true // Enable real-time updates
  );
  const { addTestResult } = useTestResults(
    firebaseAuth.user?.uid || null,
    20,
    true // Enable real-time updates
  );

  // Référence pour éviter les appels répétés à logout() lors de la déconnexion Firebase
  const logoutInProgressRef = React.useRef(false);
  const legacyLoginRef = React.useRef(false); // Track if using legacy login (without Firebase)

  // Sync Firebase user with local user state
  useEffect(() => {
    if (firebaseAuth.user) {
      // Réinitialiser le flag quand un utilisateur est connecté
      logoutInProgressRef.current = false;
      // Charger le profil utilisateur depuis Firebase
      const loadUserProfile = async () => {
        try {
          const { getUserById } = await import("../services/firebase/userService");
          const existingUser = await getUserById(firebaseAuth.user!.uid);

          if (existingUser) {
            // Utiliser le profil existant depuis Firebase
            setUser(existingUser);

            // Synchroniser le statut d'évaluation dans localStorage
            const { getUserAssessmentStatus } = await import("../services/firebase/userService");
            const assessed = await getUserAssessmentStatus(firebaseAuth.user!.uid);
            if (assessed) {
              localStorage.setItem("levelAssessed", "true");
            }
          } else {
            // Créer un nouveau profil si l'utilisateur n'existe pas encore
            // firebaseAuth.user est garanti non-null ici car on est dans le if (firebaseAuth.user)
            const currentUser = firebaseAuth.user!;
            const userProfile: UserProfile = {
              id: currentUser.uid,
              name: currentUser.displayName || currentUser.email || "Utilisateur",
              currentLevel: "B1",
              targetLevel: "C1",
              strengths: [],
              weaknesses: [],
              completedExercises: progress?.totalTests || 0,
              totalScore: 0,
              createdAt: new Date(currentUser.metadata.creationTime || Date.now()),
              lastActivity: new Date(),
            };
            setUser(userProfile);

            // Sync user profile to Firestore
            createOrUpdateUserProfile(currentUser.uid, {
              email: currentUser.email,
              displayName: currentUser.displayName,
              currentLevel: "B1",
              targetLevel: "C1",
              emailVerified: currentUser.emailVerified,
            }).catch((error) => console.error("Error syncing user profile:", error));
          }
        } catch (error) {
          console.error("Erreur lors du chargement du profil utilisateur:", error);
          // Fallback : créer un profil par défaut
          // firebaseAuth.user est garanti non-null ici car on est dans le if (firebaseAuth.user)
          const currentUser = firebaseAuth.user!;
          const userProfile: UserProfile = {
            id: currentUser.uid,
            name: currentUser.displayName || currentUser.email || "Utilisateur",
            currentLevel: "B1",
            targetLevel: "C1",
            strengths: [],
            weaknesses: [],
            completedExercises: progress?.totalTests || 0,
            totalScore: 0,
            createdAt: new Date(currentUser.metadata.creationTime || Date.now()),
            lastActivity: new Date(),
          };
          setUser(userProfile);
        }
      };

      loadUserProfile();
    } else if (
      firebaseAuth.user === null &&
      !firebaseAuth.loading &&
      (token || user) &&
      !logoutInProgressRef.current &&
      !legacyLoginRef.current // Don't auto-logout if using legacy auth
    ) {
      // IMPORTANT: Nettoyer l'état local quand Firebase user devient null (déconnexion)
      // Vérifier qu'il y a un état local à nettoyer et qu'on n'est pas déjà en train de nettoyer
      // Cela garantit que l'utilisateur est bien déconnecté même si logout() n'a pas été appelé explicitement
      logoutInProgressRef.current = true;
      logout();
    } else if (!token && !user) {
      // Fallback to localStorage for backward compatibility
      // Only load if state is empty to avoid loops
      const storedToken = localStorage.getItem("token");
      const storedUser = localStorage.getItem("user");

      // Check if we need to clean localStorage
      if (
        firebaseAuth.user === null &&
        !firebaseAuth.loading &&
        (storedToken || storedUser) &&
        !logoutInProgressRef.current &&
        !legacyLoginRef.current
      ) {
        // Clean localStorage if Firebase says no user
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        localStorage.removeItem("userProfile");
        localStorage.removeItem("levelAssessed");
        return;
      }

      if (storedToken && storedUser && !firebaseAuth.user) {
        try {
          const userData = JSON.parse(storedUser);
          setToken(storedToken);

          const userProfile: UserProfile = {
            id: userData.id || `user_${Date.now()}`,
            name:
              `${userData.firstName || ""} ${userData.lastName || ""}`.trim() ||
              userData.email ||
              "Utilisateur",
            currentLevel: userData.currentLevel || "B1",
            targetLevel: userData.targetLevel || "C1",
            strengths: [],
            weaknesses: userData.weaknesses || [],
            completedExercises: 0,
            totalScore: 0,
            createdAt: userData.createdAt ? new Date(userData.createdAt) : new Date(),
            lastActivity: userData.lastLogin ? new Date(userData.lastLogin) : new Date(),
          };
          setUser(userProfile);
        } catch (error) {
          console.error("Erreur chargement utilisateur:", error);
          localStorage.removeItem("token");
          localStorage.removeItem("user");
        }
      }
    }

    // Load responses from localStorage (will be migrated to Firestore gradually)
    const storedResponses = localStorage.getItem("userResponses");
    if (storedResponses) {
      try {
        setResponses(JSON.parse(storedResponses));
      } catch (error) {
        console.error("Erreur chargement réponses:", error);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firebaseAuth.user, firebaseAuth.loading]);

  useEffect(() => {
    if (user) {
      localStorage.setItem("userProfile", JSON.stringify(user));
    }
  }, [user]);

  useEffect(() => {
    // Only save to localStorage if responses is not empty (to avoid saving "[]" after logout)
    if (responses.length > 0) {
      localStorage.setItem("userResponses", JSON.stringify(responses));
    } else {
      // If responses is empty and there's no user, remove from localStorage
      if (!user) {
        localStorage.removeItem("userResponses");
      }
    }
    updateStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [responses, user]);

  const addResponse = (response: UserResponse) => {
    setResponses((prev) => [...prev, response]);

    if (user) {
      const updatedUser = {
        ...user,
        completedExercises: user.completedExercises + 1,
        lastActivity: new Date(),
      };
      setUser(updatedUser);

      // Save to Firebase if authenticated
      if (firebaseAuth.user && addTestResult) {
        const testResultPromise = addTestResult({
          exerciseId: response.exerciseId,
          questionId: response.questionId,
          answer: response.answer,
          isCorrect: response.isCorrect,
          timeSpent: response.timeSpent,
          timestamp: response.timestamp,
        });
        if (testResultPromise && typeof testResultPromise.catch === "function") {
          testResultPromise.catch((error) =>
            console.error("Error saving test result to Firebase:", error)
          );
        }
      }
    }
  };

  const updateStats = () => {
    const uniqueExercises = new Set(responses.map((r) => r.exerciseId));
    const correctResponses = responses.filter((r) => r.isCorrect).length;
    const averageScore = responses.length > 0 ? (correctResponses / responses.length) * 100 : 0;
    const timeSpent = responses.reduce((acc, r) => acc + r.timeSpent, 0);

    const newStats = {
      totalExercises: uniqueExercises.size,
      completedExercises: uniqueExercises.size,
      averageScore: Math.round(averageScore * 100) / 100,
      timeSpent,
      streakDays: calculateStreak(),
      levelProgress: {},
      domainProgress: {},
    };

    setStats(newStats);

    // Update progress in Firebase if authenticated
    if (firebaseAuth.user && user) {
      const progressPromise = updateProgress({
        totalTests: newStats.totalExercises,
        averageScore: newStats.averageScore,
        timeSpent: newStats.timeSpent,
        streakDays: newStats.streakDays,
        currentLevel: user.currentLevel,
        targetLevel: user.targetLevel,
      });
      if (progressPromise && typeof progressPromise.catch === "function") {
        progressPromise.catch((error) =>
          console.error("Error updating progress in Firebase:", error)
        );
      }
    }
  };

  const calculateStreak = (): number => {
    if (responses.length === 0) return 0;

    const sortedDates = responses
      .map((r) => new Date(r.timestamp).toDateString())
      .filter((date, index, self) => self.indexOf(date) === index)
      .sort((a, b) => new Date(b).getTime() - new Date(a).getTime());

    let streak = 0;
    let currentDate = new Date();

    for (const dateStr of sortedDates) {
      const date = new Date(dateStr);
      const diffDays = Math.floor((currentDate.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));

      if (diffDays === streak) {
        streak++;
      } else {
        break;
      }

      currentDate = date;
    }

    return streak;
  };

  const login = (newToken: string, userData: any) => {
    // Mark as legacy login (without Firebase auth)
    legacyLoginRef.current = true;

    setToken(newToken);

    // Save to localStorage
    if (typeof window !== "undefined") {
      localStorage.setItem("token", newToken);
      localStorage.setItem("user", JSON.stringify(userData));
    }

    const userProfile: UserProfile = {
      id: userData.id || `user_${Date.now()}`,
      name:
        `${userData.firstName || ""} ${userData.lastName || ""}`.trim() ||
        userData.email ||
        "Utilisateur",
      currentLevel: userData.currentLevel || "B1",
      targetLevel: userData.targetLevel || "C1",
      strengths: [],
      weaknesses: userData.weaknesses || [],
      completedExercises: 0,
      totalScore: 0,
      createdAt: userData.createdAt ? new Date(userData.createdAt) : new Date(),
      lastActivity: new Date(),
    };
    setUser(userProfile);
  };

  const logout = () => {
    // Reset legacy login flag
    legacyLoginRef.current = false;

    // Réinitialiser tous les états
    setToken(null);
    setUser(null);
    setResponses([]);
    setStats({
      totalExercises: 0,
      completedExercises: 0,
      averageScore: 0,
      timeSpent: 0,
      streakDays: 0,
      levelProgress: {},
      domainProgress: {},
    });

    // Nettoyer localStorage
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("userProfile");
    localStorage.removeItem("userResponses");
    localStorage.removeItem("levelAssessed");

    // Note: On garde levelAssessed pour permettre de ne pas refaire le test
    // mais on pourrait aussi le supprimer si nécessaire
  };

  // Firebase authentication methods
  const firebaseLogin = async (email: string, password: string) => {
    const result = await firebaseAuth.login(email, password);
    return result;
  };

  const firebaseRegister = async (email: string, password: string, displayName: string) => {
    const result = await firebaseAuth.register(email, password, displayName);
    return result;
  };

  const firebaseLogout = async () => {
    // Marquer que la déconnexion est en cours pour éviter les appels répétés
    logoutInProgressRef.current = true;

    // Nettoyer l'état local IMMÉDIATEMENT avant d'appeler Firebase logout
    // Cela garantit que l'UI se met à jour même si Firebase logout échoue
    logout();
    // Force remove userResponses from localStorage (in case useEffect hasn't run yet)
    localStorage.removeItem("userResponses");

    try {
      const result = await firebaseAuth.logout();
      // Firebase logout devrait déclencher onAuthStateChange qui va maintenir l'état nettoyé
      // Le useEffect détectera que firebaseAuth.user est null et ne fera rien car logoutInProgressRef.current est true
      return result;
    } catch (error) {
      console.error("Erreur lors de la déconnexion Firebase:", error);
      // L'état local est déjà nettoyé par logout() appelé au début
      return {
        success: false,
        error: "logout_failed",
        message: "Erreur lors de la déconnexion Firebase, mais l'état local a été nettoyé",
      };
    } finally {
      // Réinitialiser le flag après un court délai pour permettre au useEffect de se stabiliser
      setTimeout(() => {
        logoutInProgressRef.current = false;
      }, 1000);
    }
  };

  const googleSignIn = async () => {
    const result = await firebaseAuth.signInWithGoogle();
    return result;
  };

  // Si Firebase est utilisé, on se fie uniquement à firebaseAuth.isAuthenticated
  // Sinon, on utilise l'authentification legacy (token + user)
  const isAuthenticated = firebaseAuth.isAuthenticated || (!!token && !!user && !firebaseAuth.user);

  return (
    <UserContext.Provider
      value={{
        user,
        token,
        isAuthenticated,
        loading: firebaseAuth.loading,
        error: firebaseAuth.error,
        login,
        firebaseLogin,
        firebaseRegister,
        firebaseLogout,
        googleSignIn,
        logout,
        setUser,
        responses,
        addResponse,
        stats,
        updateStats,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUser = (): UserContextType => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
};
